import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  ClipboardDocumentIcon,
  ArrowPathIcon,
  TrashIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  telegramLinksApi,
  type TelegramLink,
  type TelegramLinkPermissions,
} from '../../api/telegramLinksApi';
import { CardContainer, PrimaryButton, AppIconButton } from '../ui';
import ToggleSwitch from '../ToggleSwitch';

export interface StaffOption {
  id: number;
  name: string;
}

interface TelegramAccessPanelProps {
  /** Staff roster used to attach a link to an existing staff member. */
  staff?: StaffOption[];
}

const PERMISSION_FIELDS: { key: keyof TelegramLinkPermissions; label: string }[] = [
  { key: 'canExpenses', label: 'Expenses' },
  { key: 'canPayables', label: 'Payables' },
  { key: 'canAggregator', label: 'Aggregator' },
  { key: 'canInventory', label: 'Inventory' },
];

const BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;

const emptyPerms = (): TelegramLinkPermissions => ({
  canExpenses: false,
  canPayables: false,
  canAggregator: false,
  canInventory: false,
});

const TelegramAccessPanel = ({ staff = [] }: TelegramAccessPanelProps) => {
  const { theme } = useTheme();
  const confirm = useConfirm();

  const [links, setLinks] = useState<TelegramLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [selectedStaffId, setSelectedStaffId] = useState<number | ''>('');
  const [displayName, setDisplayName] = useState('');
  const [newPerms, setNewPerms] = useState<TelegramLinkPermissions>(emptyPerms());

  const border = theme.components?.card?.border || theme.gray?.[200] || '#E5E5E3';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await telegramLinksApi.list();
      setLinks(data);
    } catch {
      toast.error('Could not load Telegram links.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const canCreate = selectedStaffId !== '' || displayName.trim().length > 0;

  const handleCreate = async () => {
    if (!canCreate || creating) return;
    setCreating(true);
    try {
      const { data } = await telegramLinksApi.create({
        ...(selectedStaffId !== '' ? { staffMemberId: selectedStaffId } : { displayName: displayName.trim() }),
        ...newPerms,
      });
      setLinks((prev) => [data, ...prev]);
      setSelectedStaffId('');
      setDisplayName('');
      setNewPerms(emptyPerms());
      toast.success('Telegram invite created.');
    } catch {
      toast.error('Could not create the invite.');
    } finally {
      setCreating(false);
    }
  };

  const handleTogglePermission = async (link: TelegramLink, key: keyof TelegramLinkPermissions) => {
    const next = !link[key];
    // Optimistic flip; revert on failure.
    setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, [key]: next } : l)));
    try {
      const { data } = await telegramLinksApi.updatePermissions(link.id, { [key]: next });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? data : l)));
    } catch {
      setLinks((prev) => prev.map((l) => (l.id === link.id ? { ...l, [key]: link[key] } : l)));
      toast.error('Could not update permissions.');
    }
  };

  const handleRegenerate = async (link: TelegramLink) => {
    setBusyId(link.id);
    try {
      const { data } = await telegramLinksApi.regenerate(link.id);
      setLinks((prev) => prev.map((l) => (l.id === link.id ? data : l)));
      toast.success('New code generated.');
    } catch {
      toast.error('Could not regenerate the code.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeactivate = async (link: TelegramLink) => {
    const ok = await confirm({
      title: 'Remove Telegram access?',
      description: `${link.displayName} will lose bot access immediately.`,
      confirmText: 'Remove',
    });
    if (!ok) return;
    setBusyId(link.id);
    try {
      await telegramLinksApi.deactivate(link.id);
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
      toast.success('Access removed.');
    } catch {
      toast.error('Could not remove access.');
    } finally {
      setBusyId(null);
    }
  };

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard?.writeText(code);
      toast.success('Code copied.');
    } catch {
      toast.error('Could not copy the code.');
    }
  };

  const deepLink = (code: string) =>
    BOT_USERNAME ? `https://t.me/${BOT_USERNAME}?start=${code}` : null;

  const activeLinks = useMemo(() => links.filter((l) => l.isActive), [links]);

  return (
    <CardContainer disableHover sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <PaperAirplaneIcon width={20} color={theme.primary} />
        <Typography sx={{ fontWeight: 700, color: theme.text }}>Telegram access</Typography>
      </Box>
      <Typography sx={{ color: theme.gray?.[600], fontSize: '0.85rem', mb: 2 }}>
        Give staff a Telegram bot login scoped to just what they need. Share the invite code; it
        stops working once claimed.
      </Typography>

      {/* Create */}
      <Box
        data-testid="telegram-create-form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 1.5,
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${border}`,
          backgroundColor: 'rgba(255,255,255,0.4)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel id="tg-staff-label">Staff member</InputLabel>
            <Select
              labelId="tg-staff-label"
              label="Staff member"
              data-testid="telegram-staff-select"
              value={selectedStaffId === '' ? '' : String(selectedStaffId)}
              onChange={(e) => setSelectedStaffId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <MenuItem value="">
                <em>Custom name…</em>
              </MenuItem>
              {staff.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Display name"
            placeholder="e.g. Front desk"
            data-testid="telegram-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={selectedStaffId !== ''}
            sx={{ flex: 1, minWidth: 180 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {PERMISSION_FIELDS.map((p) => (
            <ToggleSwitch
              key={p.key}
              label={p.label}
              labelPosition="right"
              enabled={newPerms[p.key]}
              onChange={() => setNewPerms((prev) => ({ ...prev, [p.key]: !prev[p.key] }))}
              color={theme.primary}
              size="sm"
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryButton
            data-testid="telegram-create-btn"
            onClick={handleCreate}
            disabled={!canCreate || creating}
          >
            {creating ? 'Creating…' : 'Create invite'}
          </PrimaryButton>
        </Box>
      </Box>

      {/* List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} sx={{ color: theme.primary }} />
        </Box>
      ) : activeLinks.length === 0 ? (
        <Typography sx={{ color: theme.gray?.[500], textAlign: 'center', py: 3, fontSize: '0.9rem' }}>
          No Telegram access yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {activeLinks.map((link) => {
            const link2me = link.inviteCode ? deepLink(link.inviteCode) : null;
            return (
              <Box
                key={link.id}
                data-testid="telegram-link-row"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: `1px solid ${border}`,
                  backgroundColor: theme.components?.card?.background || '#FFFFFF',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 600, color: theme.text, flexGrow: 1 }}>
                    {link.displayName}
                  </Typography>
                  <Chip
                    size="small"
                    label={link.claimed ? 'Claimed' : 'Pending'}
                    sx={{
                      backgroundColor: link.claimed ? `${theme.success}22` : `${theme.warning || '#d97706'}22`,
                      color: link.claimed ? theme.success : theme.warning || '#d97706',
                      fontWeight: 600,
                    }}
                  />
                </Box>

                {link.claimed ? (
                  <Typography sx={{ fontSize: '0.8rem', color: theme.gray?.[600], mt: 0.5 }}>
                    {link.telegramUsername ? `@${link.telegramUsername}` : 'Telegram user'}
                    {link.claimedAt ? ` · claimed ${new Date(link.claimedAt).toLocaleDateString()}` : ''}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                    <Box
                      component="code"
                      data-testid="telegram-invite-code"
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: theme.gray?.[100] || '#f3f3f1',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        color: theme.text,
                      }}
                    >
                      {link.inviteCode}
                    </Box>
                    <AppIconButton
                      icon={<ClipboardDocumentIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Copy code"
                      variant="secondary"
                      onClick={() => link.inviteCode && copyCode(link.inviteCode)}
                      sx={{ width: 32, height: 32 }}
                    />
                    {link2me ? (
                      <Typography
                        component="a"
                        href={link2me}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="telegram-deep-link"
                        sx={{ fontSize: '0.8rem', color: theme.primary, textDecoration: 'underline' }}
                      >
                        Open in Telegram
                      </Typography>
                    ) : (
                      <Typography sx={{ fontSize: '0.8rem', color: theme.gray?.[500] }}>
                        share this code
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Inline permission toggles */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.25 }}>
                  {PERMISSION_FIELDS.map((p) => (
                    <ToggleSwitch
                      key={p.key}
                      label={p.label}
                      labelPosition="right"
                      enabled={link[p.key]}
                      onChange={() => handleTogglePermission(link, p.key)}
                      disabled={busyId === link.id}
                      color={theme.primary}
                      size="sm"
                    />
                  ))}
                </Box>

                {/* Row actions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  {!link.claimed && (
                    <AppIconButton
                      icon={<ArrowPathIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Regenerate code"
                      variant="secondary"
                      onClick={() => handleRegenerate(link)}
                      disabled={busyId === link.id}
                      sx={{ width: 32, height: 32 }}
                    />
                  )}
                  <AppIconButton
                    icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                    tooltip="Remove access"
                    variant="danger"
                    onClick={() => handleDeactivate(link)}
                    disabled={busyId === link.id}
                    sx={{ width: 32, height: 32 }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </CardContainer>
  );
};

export default TelegramAccessPanel;
